import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Wand2,
  RotateCcw,
  Beaker,
  Gauge,
  Sliders,
  ShieldCheck,
  Clock,
  Ban,
  Copy,
  RotateCw,
  Archive,
  Languages,
} from "lucide-react";

const MODES = [
  {
    key: "cordialement",
    color: "#B5EAD7",
    icon: <Wand2 className="w-4 h-4" strokeWidth={2.5} />,
    title: "Cordialement",
    tagline: "Ce que je pense → ce que je peux envoyer.",
    body: "Le mode historique. Colle ton message brut (celui que tu as vraiment envie d'écrire) et cordialement.ai en produit une version civilisée, prête à partir.",
    subFeatures: [
      {
        label: "Sous-mode",
        detail: "Reformuler (message que tu envoies) ou Répondre (message que tu as reçu). Deux placeholders différents pour t'orienter.",
      },
      {
        label: "5 tons au choix",
        detail: "N1 Diplomatique · N2 Professionnel · N3 Assertif · N4 Humour subtil · N5 Passif-agressif élégant.",
      },
      {
        label: "Curseur de diplomatie 0 → 100",
        detail: "Ancré sur 5 crans : On assume · Direct · Professionnel · Diplomatique · Secrétaire ONU. Le prompt est réellement modulé — plus la valeur est basse, plus la formulation est nette et sans fioriture ; plus elle est haute, plus la version est hédgée et solennelle.",
      },
    ],
  },
  {
    key: "reverse",
    color: "#FFDAC1",
    icon: <RotateCcw className="w-4 h-4" strokeWidth={2.5} />,
    title: "Reverse",
    tagline: "Ce que je peux envoyer → ce que j'aurais préféré écrire.",
    body: "L'exact inverse de Cordialement. Colle un message professionnel diplomatique : cordialement.ai en produit la version « pensée intérieure », débarrassée du vernis corporate. Cathartique et satirique, pas destiné à être envoyé.",
    subFeatures: [
      {
        label: "3 niveaux de franchise",
        detail: "I Franc · II Ça commence à suffire · III Bien cordialement dans ta gueule. Le troisième niveau reste mordant mais publiable : aucune menace, aucune insulte discriminatoire, aucune accusation d'une infraction précise.",
      },
      {
        label: "Sortie encadrée",
        detail: "Chaque réponse commence par « Traduction non contractuelle de ta pensée intérieure : » — c'est une interprétation satirique assumée, pas une analyse psychologique.",
      },
    ],
  },
  {
    key: "antidote",
    color: "#C7CEEA",
    icon: <Beaker className="w-4 h-4" strokeWidth={2.5} />,
    title: "Antidote",
    tagline: "Le corporate traduit en français humain.",
    body: "Utilité pure. Colle un email pompeux, une invitation Teams cryptique ou une note interne alambiquée. cordialement.ai décode ce que le message dit vraiment, ce qu'on te demande et pour quand.",
    subFeatures: [
      {
        label: "En clair",
        detail: "Résumé du message en 1 à 3 phrases simples, sans jargon.",
      },
      {
        label: "Ce qu'on te demande",
        detail: "L'action concrète attendue de ta part, en une phrase.",
      },
      {
        label: "Pour quand",
        detail: "La deadline extraite du message, exclusivement si elle est réellement présente. Sinon : « Pas précisé. » — jamais d'invention.",
      },
      {
        label: "Jargon détecté",
        detail: "Les expressions corporate réellement utilisées dans le texte source, listées en petits chips monospace.",
      },
    ],
  },
  {
    key: "bullshit",
    color: "#FFF2B2",
    icon: <Gauge className="w-4 h-4" strokeWidth={2.5} />,
    title: "Bullshit Score",
    tagline: "Mesure le taux de jargon d'un message.",
    body: "Ludique et satirique. Colle un message reçu (ou rédigé) : cordialement.ai le note de 0 à 100 sur son taux de bullshit corporate. Diagnostic non homologué ISO 0000.",
    subFeatures: [
      {
        label: "Score 0 → 100",
        detail: "Jauge visuelle qui se teinte du vert au rouge selon la densité de jargon, d'anglicismes, de mots creux et de tournures inutilement complexes.",
      },
      {
        label: "Jargon détecté",
        detail: "Jusqu'à 15 expressions corporate réellement présentes, dédupliquées côté serveur.",
      },
      {
        label: "En français",
        detail: "La version simplifiée du même message en langage humain.",
      },
      {
        label: "Ce qu'il fallait écrire",
        detail: "La version courte, claire et actionnable qu'il aurait fallu envoyer d'entrée.",
      },
      {
        label: "Diagnostic",
        detail: "Une phrase éditoriale sèche qui résume la performance corporate.",
      },
    ],
  },
];

const CONTROLS = [
  {
    icon: <Sliders className="w-4 h-4" strokeWidth={2.5} />,
    title: "Réglages contextuels",
    body: "Les réglages n'apparaissent QUE quand ils sont utiles : le curseur de diplomatie est visible uniquement en mode Cordialement, les 3 niveaux de franchise uniquement en Reverse. Antidote et Bullshit Score n'ont pas de tonalité à choisir — le résultat est structuré.",
  },
  {
    icon: <Copy className="w-4 h-4" strokeWidth={2.5} />,
    title: "Copier / Régénérer",
    body: "Chaque panneau de résultat dispose d'un bouton Copier (texte formaté prêt à coller) et Régénérer (relance avec les mêmes paramètres). Le tampon « STATUT : ENVOYABLE SANS CONVOCATION RH » signale les sorties Cordialement.",
  },
  {
    icon: <Languages className="w-4 h-4" strokeWidth={2.5} />,
    title: "Bilingue FR / EN",
    body: "Bouton en haut à droite pour basculer entre français et anglais. Toute l'interface, les placeholders et les résultats suivent la langue sélectionnée. La préférence est mémorisée localement.",
  },
];

const PRIVACY_POINTS = [
  {
    label: "Aucun compte, aucune inscription",
    detail: "L'application ne demande jamais votre email, votre nom ou un mot de passe.",
  },
  {
    label: "Aucune sauvegarde serveur du contenu",
    detail: "Votre message et la réponse générée ne sont pas enregistrés dans notre base. Seul un compteur entier (nombre total de générations) est incrémenté pour le quota communautaire.",
  },
  {
    label: "Historique 100 % local",
    detail: "Vos 20 dernières reformulations sont conservées uniquement dans le localStorage de votre navigateur. Bouton « Purger les archives » pour tout effacer.",
  },
  {
    label: "Préférences UX en local",
    detail: "Le dernier mode, la position du curseur de diplomatie, le niveau de franchise Reverse et la langue sont stockés dans votre navigateur — jamais envoyés en base.",
  },
  {
    label: "Aucun tracker analytics",
    detail: "Zéro Google Analytics, PostHog, Meta Pixel, Hotjar ou équivalent. Aucun cookie applicatif.",
  },
];

const LIMITATIONS = [
  "Les requêtes hors périmètre (recettes, code, devoirs, conseils juridiques/médicaux) sont poliment refusées par l'IA. cordialement.ai est spécialisé uniquement dans la communication professionnelle.",
  "L'IA peut se tromper, mal comprendre le contexte ou produire un ton inadapté. Relis toujours avant d'envoyer. Vous restez responsable du contenu envoyé.",
  "Ne saisissez pas d'informations sensibles, confidentielles ou nominatives qui ne sont pas strictement nécessaires : votre texte transite par l'API OpenAI, qui peut le conserver jusqu'à 30 jours à des fins de sécurité (voir mentions légales).",
  "Le mode Reverse niveau III (« Bien cordialement dans ta gueule ») est satirique. Les réponses sont mordantes mais restent publiables — sans menace, sans insulte discriminatoire, sans accusation d'infraction.",
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-[#FBF9F6] grain">
      <header className="border-b-2 border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="howto-back"
            className="text-sm font-medium flex items-center gap-2 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </Link>
          <h1 className="font-display font-semibold text-base sm:text-xl uppercase tracking-tight">
            cordialement<span className="text-[#4A4A4A]">.</span>ai
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-12 sm:py-16 relative z-10">
        {/* Editorial kicker */}
        <div className="flex items-center gap-3 mb-6">
          <span className="dossier-tag">Mode d&apos;emploi</span>
          <span className="flex-1 h-px bg-black/20 max-w-[160px]" />
          <span className="dossier-tag">Dossier n° CDLT-2026 · V2</span>
        </div>

        <div className="mb-14 animate-slide-up">
          <h2 className="hero-title text-black text-[38px] sm:text-[54px] lg:text-[68px]">
            <span className="block font-bold">Quatre outils.</span>
            <span className="block italic font-semibold text-[#2A4A3E]">
              Une seule boîte de survie.
            </span>
          </h2>
          <p className="mt-6 text-base text-[#4A4A4A] leading-[1.6] max-w-2xl">
            cordialement.ai est la trousse de secours du Télétravailleur face au corporate.
            Quatre modes, une seule règle : utilité d&apos;abord, humour ensuite. Aucune
            inscription, aucun tracker, aucun stockage serveur de vos messages.
          </p>
        </div>

        <div className="rule-editorial mb-10 opacity-60" />

        {/* Section 1 — les 4 modes */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="dossier-tag font-bold">Section 01 · Les 4 modes</span>
            <span className="flex-1 h-px bg-black/15" />
          </div>

          <div className="space-y-6">
            {MODES.map((m) => (
              <article
                key={m.key}
                className="bg-white border-2 border-black nb-shadow p-5 sm:p-6 relative"
                data-testid={`howto-mode-${m.key}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <span
                    className="w-9 h-9 border-2 border-black flex items-center justify-center shrink-0"
                    style={{ background: m.color }}
                  >
                    {m.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-display font-bold text-2xl sm:text-3xl leading-tight tracking-tight uppercase">
                        {m.title}
                      </h3>
                    </div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                      {m.tagline}
                    </p>
                  </div>
                </div>
                <p className="text-sm sm:text-[15px] text-[#0A0A0A] leading-relaxed mb-4">
                  {m.body}
                </p>
                <ul className="space-y-2.5 border-l-2 border-black/40 pl-4">
                  {m.subFeatures.map((f) => (
                    <li key={f.label} className="text-sm text-[#4A4A4A] leading-relaxed">
                      <span className="font-mono text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A] block">
                        {f.label}
                      </span>
                      <span>{f.detail}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        {/* Section 2 — Réglages transverses */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="dossier-tag font-bold">Section 02 · Contrôles transverses</span>
            <span className="flex-1 h-px bg-black/15" />
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {CONTROLS.map((c) => (
              <article
                key={c.title}
                className="bg-white border-2 border-black nb-shadow-sm p-5"
                data-testid={`howto-control-${c.title.replace(/\W+/g, "-").toLowerCase()}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 bg-[#FBF9F6] border-2 border-black flex items-center justify-center">
                    {c.icon}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-lg leading-tight tracking-tight mb-2">
                  {c.title}
                </h3>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Section 3 — Quota */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="dossier-tag font-bold">Section 03 · Quota communautaire</span>
            <span className="flex-1 h-px bg-black/15" />
          </div>
          <article
            className="bg-white border-2 border-black nb-shadow p-5 sm:p-6"
            data-testid="howto-quota"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="w-9 h-9 bg-[#FBF9F6] border-2 border-black flex items-center justify-center">
                <Clock className="w-4 h-4" strokeWidth={2.5} />
              </span>
              <h3 className="font-display font-semibold text-xl leading-tight tracking-tight">
                80 générations par jour, partagées par toute la communauté.
              </h3>
            </div>
            <ul className="space-y-2 text-sm text-[#4A4A4A] leading-relaxed border-l-2 border-black/40 pl-4">
              <li>
                <strong>Un appel LLM = une génération.</strong> Peu importe le mode
                (Cordialement, Reverse, Antidote, Bullshit Score), chaque bouton
                &laquo; Civiliser / Dé-cordialiser / Décoder / Diagnostiquer &raquo;
                consomme une seule unité.
              </li>
              <li>
                <strong>Compteur visible</strong> en haut à droite (ex : &laquo; 73/80
                restantes &raquo;). Réinitialisation quotidienne à minuit UTC.
              </li>
              <li>
                <strong>Premier arrivé, premier servi.</strong> Le service peut être
                temporairement saturé en fin de journée. Reviens le lendemain.
              </li>
              <li>
                Les actions <strong>sans appel IA</strong> (copier, régénérer sans
                changement, ouvrir les archives, changer de mode, jouer avec le curseur)
                ne consomment aucune unité.
              </li>
            </ul>
          </article>
        </section>

        {/* Section 4 — Historique local */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="dossier-tag font-bold">Section 04 · Historique local</span>
            <span className="flex-1 h-px bg-black/15" />
          </div>
          <article
            className="bg-white border-2 border-black nb-shadow p-5 sm:p-6"
            data-testid="howto-history"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="w-9 h-9 bg-[#FBF9F6] border-2 border-black flex items-center justify-center">
                <Archive className="w-4 h-4" strokeWidth={2.5} />
              </span>
              <h3 className="font-display font-semibold text-xl leading-tight tracking-tight">
                20 dernières générations, entièrement locales.
              </h3>
            </div>
            <p className="text-sm text-[#4A4A4A] leading-relaxed mb-3">
              Bouton <strong>&laquo; Archives &raquo;</strong> dans l&apos;en-tête :
              tes 20 dernières générations (tous modes confondus) sont conservées dans
              le <code className="bg-[#F3EFE7] px-1.5 py-0.5 border border-black/20 font-mono text-[12px]">localStorage</code>
              {" "}de ton navigateur. Cliquer sur une archive la recharge dans le mode
              d&apos;origine avec ses réglages exacts.
            </p>
            <p className="text-sm text-[#4A4A4A] leading-relaxed">
              Rien n&apos;est envoyé ni synchronisé avec un compte. Le bouton
              <strong> &laquo; Purger les archives &raquo;</strong> efface tout définitivement.
              Vider les données du site depuis ton navigateur produit le même effet.
            </p>
          </article>
        </section>

        {/* Section 5 — Privacy pratique */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="dossier-tag font-bold">Section 05 · Confidentialité en pratique</span>
            <span className="flex-1 h-px bg-black/15" />
          </div>
          <div className="bg-[#B5EAD7] border-2 border-black nb-shadow p-6" data-testid="howto-privacy">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-5 h-5" strokeWidth={2.5} />
              <span className="dossier-tag text-black">Cadre d&apos;utilisation</span>
            </div>
            <p className="font-display font-semibold text-2xl leading-tight tracking-tight mb-4">
              Gratuit, anonyme, privacy-first.
            </p>
            <ul className="space-y-3 text-sm text-[#0A0A0A] leading-relaxed">
              {PRIVACY_POINTS.map((p) => (
                <li key={p.label}>
                  <span className="font-mono text-[10px] uppercase tracking-widest font-bold block mb-0.5">
                    {p.label}
                  </span>
                  <span className="text-[#0A0A0A]/85">{p.detail}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#0A0A0A]/70 italic mt-4 leading-relaxed">
              Détails techniques complets dans la page{" "}
              <Link to="/legal" className="underline font-medium">
                Mentions légales &amp; confidentialité
              </Link>.
            </p>
          </div>
        </section>

        {/* Section 6 — Restrictions */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <Ban className="w-4 h-4" strokeWidth={2.5} />
            <span className="dossier-tag font-bold">Section 06 · Restrictions d&apos;usage</span>
            <span className="flex-1 h-px bg-black/15" />
          </div>
          <ul className="space-y-3 text-sm text-[#4A4A4A] leading-relaxed border-l-2 border-black/40 pl-5">
            {LIMITATIONS.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </section>

        <div className="mt-14 text-center">
          <Link
            to="/"
            data-testid="howto-cta"
            className="inline-flex items-center gap-2 bg-[#B5EAD7] border-2 border-black nb-shadow nb-hover px-6 py-3 font-semibold text-base"
          >
            <RotateCw className="w-4 h-4" strokeWidth={2.5} />
            Ouvrir la machine
          </Link>
        </div>
      </main>
    </div>
  );
}
